import { HOST_TAG, TAG_TAG, isDarkSurface } from "./dom";
import { dict, titleFor, type Lang } from "./i18n";
import type { MarkKind, Origin, PublicPolicy } from "./shared";
import { euIconSvg, type EuIconKind } from "./eu-icons";
import { css } from "./styles";

/** Everything the reader card needs to know about one labelled element. */
export interface Label {
  kind: MarkKind;
  origin: Origin;
  tool: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string | null;
}

type LabelPolicy = Pick<PublicPolicy, "theme" | "accent" | "labelStyle" | "euTone" | "textPlacement" | "labelTone" | "reviewerTitle" | "showDetails">;

/** EU icon to use for an origin: fully generated vs partially modified. */
export const euKind = (origin: Origin): EuIconKind => (origin === "AI_EDITED" ? "modified" : "generated");

/** Static SVG markup (no user data) wrapped in a span. */
function iconEl(svg: string, cls = "") {
  const span = document.createElement("span");
  if (cls) span.className = cls;
  span.innerHTML = svg;
  return span;
}

type Attrs = Record<string, string | boolean | undefined | ((e: Event) => void)>;

/** Tiny element factory. Children strings are set as text (never HTML). */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs = {}, ...children: Array<Node | string | null | false>) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (typeof v === "function") el.addEventListener(k.replace(/^on/, "").toLowerCase(), v as EventListener);
    else if (k === "class") el.className = String(v);
    else el.setAttribute(k, v === true ? "" : v);
  }
  for (const c of children) if (c) el.append(c);
  return el;
}

export class Layer {
  host: HTMLElement;
  root: ShadowRoot;
  view: HTMLDivElement;
  private styleEl: HTMLStyleElement;
  private card: HTMLElement | null = null;
  dark: boolean;

  constructor(policy: Pick<PublicPolicy, "theme" | "accent">) {
    this.dark = policy.theme === "dark";
    this.host = document.createElement(HOST_TAG);
    this.host.setAttribute("style", "position:absolute;top:0;left:0;width:0;height:0;z-index:2147483000;display:block");
    this.root = this.host.attachShadow({ mode: "open" });
    this.styleEl = h("style");
    this.styleEl.textContent = css(policy);
    this.view = h("div", { class: "v" });
    this.root.append(this.styleEl, this.view);
    document.documentElement.appendChild(this.host);
    document.addEventListener("keydown", (e) => e.key === "Escape" && this.closeCard());
    document.addEventListener("click", (e) => {
      if (this.card && !e.composedPath().includes(this.host) && !(e.target as Element)?.closest?.(TAG_TAG)) this.closeCard();
    });
  }

  setTheme(policy: Pick<PublicPolicy, "theme" | "accent">) {
    this.dark = policy.theme === "dark";
    this.styleEl.textContent = css(policy);
  }

  closeCard() {
    this.card?.remove();
    this.card = null;
  }

  showCard(anchor: Element, label: Label, ctx: { lang: Lang; agency?: string; siteName?: string; transparencyUrl?: string; eu?: boolean; explainer?: string }) {
    this.closeCard();
    const d = dict(ctx.lang);
    const fmt = (iso: string | null) =>
      iso ? new Intl.DateTimeFormat(ctx.lang === "pl" ? "pl-PL" : "en-GB", { dateStyle: "medium" }).format(new Date(iso)) : null;

    const rows: HTMLElement[] = [];
    const row = (k: string, v: string | null, cls?: string) => v && rows.push(h("div", { class: "row" }, h("span", {}, k), h("span", { class: cls }, v)));
    row(d.tool, label.origin === "HUMAN" ? null : label.tool);
    row(d.publisher, ctx.agency || ctx.siteName || null);
    row(d.marked, fmt(label.createdAt));
    if (label.reviewedBy) row(d.reviewed, `✓ ${label.reviewedBy}`, "ok");

    const card = h(
      "div",
      { class: "card", role: "dialog", "aria-label": titleFor(d, label.kind, label.origin) },
      h("button", { class: "x", "aria-label": d.close, onclick: () => this.closeCard() }, "×"),
      h(
        "h4",
        {},
        ctx.eu && label.origin !== "HUMAN"
          ? iconEl(euIconSvg(label.kind === "IMAGE" ? euKind(label.origin) : "basic", this.dark ? "white" : "black", label.kind === "IMAGE" ? 20 : 24), "eu")
          : h("span", { class: "ic" }, label.origin === "HUMAN" ? "✎" : "✦"),
        titleFor(d, label.kind, label.origin),
      ),
      ctx.explainer && label.origin !== "HUMAN" ? h("p", { class: "expl" }, ctx.explainer) : null,
      ...rows,
      ctx.eu && label.origin !== "HUMAN" ? h("p", { class: "eunote" }, d.euNote) : null,
      h(
        "div",
        { class: "foot" },
        ctx.transparencyUrl ? h("a", { href: ctx.transparencyUrl, target: "_blank", rel: "noopener" }, `${d.more} →`) : h("span", {}, d.legal),
        h("span", { class: "brand" }, "AIMARKER"),
      ),
    );
    this.view.append(card);
    this.card = card;

    const r = anchor.getBoundingClientRect();
    const w = card.offsetWidth;
    const left = Math.min(Math.max(8, r.left + window.scrollX), window.scrollX + document.documentElement.clientWidth - w - 8);
    const below = r.bottom + 8 + card.offsetHeight < window.innerHeight || r.top < card.offsetHeight + 16;
    const top = below ? r.bottom + window.scrollY + 8 : r.top + window.scrollY - card.offsetHeight - 8;
    card.style.left = `${left}px`;
    card.style.top = `${top}px`;
    (card.querySelector(".x") as HTMLElement)?.focus({ preventScroll: true });
  }
}

/** Badge floating over an image, positioned in document coordinates. */
export function imageBadge(lang: Lang, origin: Origin, onOpen: (btn: HTMLElement) => void, policy?: LabelPolicy) {
  const d = dict(lang);
  if (policy?.labelStyle !== "aimarker" && origin !== "HUMAN") {
    const details = policy?.showDetails !== false;
    // Without the details card the badge is a plain labelled image, not a button
    const eu = details
      ? h("button", { class: "eub", type: "button", "aria-label": `${d.title.IMAGE[origin]}. ${d.details}`, title: d.title.IMAGE[origin] })
      : h("span", { class: "eub static", role: "img", "aria-label": d.title.IMAGE[origin], title: d.title.IMAGE[origin] });
    // The "AI generated / AI modified" variants carry English words; other languages get the language-neutral "AI" icon
    eu.append(iconEl(euIconSvg(lang === "en" ? euKind(origin) : "basic", !policy?.euTone || policy.euTone === "auto" ? "black" : policy.euTone, lang === "en" ? 22 : 26)));
    if (details)
      eu.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen(eu);
      });
    return eu;
  }
  const btn = h(
    "button",
    { class: "badge", type: "button", "aria-label": d.title.IMAGE[origin] },
    h("span", { class: "dot" }),
    d.badge[origin],
  );
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    onOpen(btn);
  });
  return btn;
}

export function placeBadge(badge: HTMLElement, img: Element, pos: PublicPolicy["badgePosition"]) {
  const r = img.getBoundingClientRect();
  const visible = r.width >= 48 && r.height >= 32 && getComputedStyle(img).visibility !== "hidden";
  badge.style.display = visible ? "" : "none";
  if (!visible) return;
  const pad = 8;
  const bw = badge.offsetWidth || 40;
  const bh = badge.offsetHeight || 22;
  const x = pos.endsWith("right") ? r.right - bw - pad : r.left + pad;
  const y = pos.startsWith("bottom") ? r.bottom - bh - pad : r.top + pad;
  badge.style.left = `${x + window.scrollX}px`;
  badge.style.top = `${y + window.scrollY}px`;
}

/**
 * Label line for a text block or a whole article. Rendered as its own block above (or below) the content,
 * never inside the sentence, so it reads as a disclosure and does not break the paragraph.
 */
export function textTag(policy: LabelPolicy, lang: Lang, origin: Origin, onOpen: (el: HTMLElement) => void, article = false, reviewed = false, ref?: Element, siteWide = false) {
  const d = dict(lang);
  const host = document.createElement(TAG_TAG);
  // Colours come from the labelled element itself (not the container), so the label stays readable on hero photos
  const cs = ref ? getComputedStyle(ref) : null;
  const dark = ref ? isDarkSurface(ref) : policy.theme === "dark";
  const align = cs && /center|end|right/.test(cs.textAlign) ? (cs.textAlign === "center" ? "center" : "flex-end") : "flex-start";
  const color = cs?.color ?? "inherit";
  // Body font of the surrounding section (headlines often use a display serif that looks odd at label size)
  const font = ref?.parentElement ? getComputedStyle(ref.parentElement).fontFamily.replace(/[;{}]/g, "") : "inherit";
  const below0 = !article && policy.textPlacement === "end";
  // Margins live on the host (outside the shadow root) so they also work in flex/grid hero layouts
  const margin = article ? "14px 0" : below0 ? "8px 0 12px" : "12px 0 8px";
  // !important: inline important declarations win over theme rules (some themes style aimarker-tag themselves)
  host.setAttribute("style", `all:initial;display:block !important;margin:${margin} !important;color:${color} !important;font-family:${font};font-size:16px !important`);
  host.setAttribute("data-aimarker-surface", dark ? "dark" : "light");
  const root = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = `${css(policy)}\n:host{all:initial;display:block;color:inherit}\n.blk,.art{justify-content:${align}}`;
  const below = !article && policy.textPlacement === "end";
  const wrap = (child: HTMLElement) => h("span", { class: article ? `v art ${dark ? "on-dark" : "on-light"}` : `v blk${below ? " below" : ""} ${dark ? "on-dark" : "on-light"}` }, child);
  // Gentle tone (e.g. healthcare): still discloses AI, adds who verified the content when it was approved
  const soft = policy.labelTone === "soft" && origin !== "HUMAN";
  const text = soft
    ? `${siteWide ? d.soft.page : article ? d.soft.article : d.soft.text}${reviewed && policy.reviewerTitle ? ` · ${d.soft.verified(policy.reviewerTitle)}` : ""}`
    : siteWide
      ? d.pageLabel[origin]
      : article
        ? d.title.ARTICLE[origin]
        : d.textLabel[origin];
  if (policy.labelStyle !== "aimarker" && origin !== "HUMAN") {
    const details = policy.showDetails !== false;
    const eu = details ? h("button", { class: "eut", type: "button", "aria-label": `${text}. ${d.details}` }) : h("span", { class: "eut static" });
    const tone = policy.euTone === "auto" ? (dark ? "white" : "black") : policy.euTone;
    eu.append(iconEl(euIconSvg("basic", tone, article ? 22 : 18), "ei"), h("span", { class: "tx" }, text));
    if (details)
      eu.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen(host);
      });
    root.append(style, wrap(eu));
    return host;
  }
  const btn = h(
    "button",
    { class: "pill", type: "button", "aria-label": d.title.TEXT[origin] },
    h("span", { class: "dot" }),
    d.badge[origin],
  );
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    onOpen(host);
  });
  if (article) btn.append(` · ${text}`);
  root.append(style, wrap(btn));
  return host;
}
